/**
 * Diálogo de etapa da frente (#365, D3): adiciona uma etapa (nome e prazo previsto) ou corrige
 * uma existente, quando também se pode ajustar as datas reais. É da aba Etapas; quem abre decide
 * o que fazer depois de gravar (`onSaved`).
 */
import React, { useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';

import { workFrontService } from '../../../services/workFrontService';
import type { WorkFrontStageInput, WorkFrontStageItem } from '../../../types/workFront';

const TOUCH_TARGET = 44;

export interface StageDialogProps {
  open: boolean;
  frontId: number;
  /** Com uma etapa, o diálogo corrige em vez de adicionar. */
  stage?: WorkFrontStageItem;
  onClose: () => void;
  onSaved: (stage: WorkFrontStageItem) => void;
}

interface StageFieldErrors {
  name?: string;
  plannedStart?: string;
  plannedEnd?: string;
  actualEnd?: string;
}

function validateStage(values: WorkFrontStageInput): StageFieldErrors {
  const errors: StageFieldErrors = {};
  if (!values.name.trim()) errors.name = 'Nome da etapa é obrigatório';
  if (!values.plannedStart) errors.plannedStart = 'Início previsto é obrigatório';
  if (!values.plannedEnd) errors.plannedEnd = 'Fim previsto é obrigatório';
  else if (values.plannedStart && values.plannedEnd < values.plannedStart) {
    errors.plannedEnd = 'O fim previsto não pode ser antes do início';
  }
  if (values.actualStart && values.actualEnd && values.actualEnd < values.actualStart) {
    errors.actualEnd = 'O fim real não pode ser antes do início real';
  }
  return errors;
}

interface StageFormProps extends Omit<StageDialogProps, 'open'> {
  titleId: string;
}

const StageForm: React.FC<StageFormProps> = ({ frontId, stage, onClose, onSaved, titleId }) => {
  const editing = Boolean(stage);
  const [name, setName] = useState(stage?.name ?? '');
  const [plannedStart, setPlannedStart] = useState(stage?.plannedStart ?? '');
  const [plannedEnd, setPlannedEnd] = useState(stage?.plannedEnd ?? '');
  const [actualStart, setActualStart] = useState(stage?.actualStart ?? '');
  const [actualEnd, setActualEnd] = useState(stage?.actualEnd ?? '');
  const [fieldErrors, setFieldErrors] = useState<StageFieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const base: WorkFrontStageInput = { name: name.trim(), plannedStart, plannedEnd };
    const data: WorkFrontStageInput = editing
      ? { ...base, actualStart: actualStart || null, actualEnd: actualEnd || null }
      : base;

    const found = validateStage(data);
    setFieldErrors(found);
    if (Object.keys(found).length > 0) return;

    setSubmitting(true);
    setError(null);
    try {
      const saved = stage
        ? await workFrontService.updateStage(frontId, stage.id, data)
        : await workFrontService.createStage(frontId, data);
      onSaved(saved);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Não foi possível gravar a etapa.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <DialogTitle id={titleId} component="h2">
        {editing ? 'Corrigir etapa' : 'Adicionar etapa'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, pt: 1 }}>
          <TextField
            label="Nome da etapa"
            required
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
            error={Boolean(fieldErrors.name)}
            helperText={fieldErrors.name}
            sx={{ gridColumn: { sm: '1 / -1' } }}
          />
          <TextField
            label="Início previsto"
            type="date"
            required
            value={plannedStart}
            onChange={(event) => setPlannedStart(event.target.value)}
            error={Boolean(fieldErrors.plannedStart)}
            helperText={fieldErrors.plannedStart}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Fim previsto"
            type="date"
            required
            value={plannedEnd}
            onChange={(event) => setPlannedEnd(event.target.value)}
            error={Boolean(fieldErrors.plannedEnd)}
            helperText={fieldErrors.plannedEnd}
            InputLabelProps={{ shrink: true }}
          />
          {editing && (
            <>
              <TextField
                label="Início real"
                type="date"
                value={actualStart}
                onChange={(event) => setActualStart(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Fim real"
                type="date"
                value={actualEnd}
                onChange={(event) => setActualEnd(event.target.value)}
                error={Boolean(fieldErrors.actualEnd)}
                helperText={fieldErrors.actualEnd}
                InputLabelProps={{ shrink: true }}
              />
            </>
          )}
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'flex-start', gap: 1 }}>
        <Button type="submit" variant="contained" disabled={submitting} sx={{ minHeight: TOUCH_TARGET }}>
          {editing ? 'Salvar correção' : 'Adicionar etapa'}
        </Button>
        <Button onClick={onClose} disabled={submitting} sx={{ minHeight: TOUCH_TARGET }}>
          Cancelar
        </Button>
      </DialogActions>
    </Box>
  );
};

export const StageDialog: React.FC<StageDialogProps> = ({ open, onClose, ...rest }) => {
  const titleId = 'etapa-titulo';
  // O formulário só existe com o diálogo aberto: cada abertura começa do zero.
  return (
    <Dialog open={open} onClose={onClose} aria-labelledby={titleId} maxWidth="sm" fullWidth>
      <StageForm {...rest} onClose={onClose} titleId={titleId} />
    </Dialog>
  );
};

export default StageDialog;
